import { X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Menu,
  Text,
  TextInput,
} from "@mantine/core";

import Employer from "../../auth/services/model/Employer";
import { useEmployerReport } from "../providers/EmployerReportProvider";
import { useToasts } from "../../../providers/notifications/ToastsContext";

interface OptionInterface {
  selected: boolean;
  value: number | string;
  label: string;
}

const EmployerReportFilter: React.FC = () => {
  const { setSelectedEmployers, employers } = useEmployerReport();
  const { showToast } = useToasts();
  const [options, setOptions] = useState<OptionInterface[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const showErrorMessage = (err: string): void =>
    showToast({
      strapiId: "employer_report_error",
      data: { message: err },
    });

  useEffect(
    () =>
      setOptions(
        employers.map((employer: Employer) => ({
          selected: true,
          value: employer.id,
          label: employer.company_name,
        }))
      ),
    [employers]
  );

  const [isOpen, setIsOpen] = useState(false);

  const toggleOption = useCallback(
    (optionId: number | string): void => {
      if (optionId === "all") {
        setOptions(
          options.map((opt: OptionInterface) => ({
            ...opt,
            selected:
              options.filter((it: OptionInterface) => it.selected).length !==
              options.length,
          }))
        );
        return;
      }

      setOptions(
        options.map((it: OptionInterface) => {
          if (it.value === optionId) {
            return {
              ...it,
              selected: !it.selected,
            };
          }
          return it;
        })
      );
    },
    [options, setOptions]
  );

  const applyFilter = (): void => {
    const selectedEmployerIds = options
      .filter((it: OptionInterface) => it.selected === true)
      .map((it: OptionInterface) => it.value);
    if (selectedEmployerIds.length === 0) {
      showErrorMessage("Please select at least one branch.");
      return;
    }
    setSelectedEmployers(selectedEmployerIds as number[]);
    setIsOpen(false);
  };

  return (
    <div style={{ padding: "5px" }}>
      <Text>Filter by branch</Text>
      <div
        style={{
          position: "sticky",
          top: "5px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setIsOpen(true)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault(); // Prevent default scroll for Space key
                setIsOpen(true);
              }
            }}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              alignItems: "center",
              padding: "5px",
              border: "1.5px solid rgb(154 154 154)",
              borderRadius: "5px",
              minWidth: "400px",
              minHeight: "40px",
              background: "#FFFFFF",
            }}
          >
            {options.filter((it: OptionInterface) => it.selected === true)
              .length > 0 && (
              <Badge
                color="dark"
                variant="filled"
                pr={3}
                size="xl"
                rightSection={
                  <ActionIcon size="xs" color="white" radius="xl">
                    <X size={20} color="white" />
                  </ActionIcon>
                }
              >
                <Text transform="none" color="white">
                  {options.filter((it: OptionInterface) => it.selected !== true)
                    .length === 0
                    ? "All Branches"
                    : `${
                        options.filter(
                          (it: OptionInterface) => it.selected === true
                        ).length
                      } branches selected`}
                </Text>
              </Badge>
            )}
          </div>
          {/* eslint-enable */}
        </div>
        <Menu
          shadow="md"
          size={400}
          position="bottom"
          opened={isOpen}
          control={<span />}
          styles={{
            itemLabel: {
              flex: 1,
            },
          }}
        >
          <Menu.Item>
            <div style={{ padding: "0 0 10px 0" }}>
              <TextInput
                placeholder="Search branches..."
                onChange={(e) => setSearchQuery(e.target.value)}
                value={searchQuery}
                style={{ width: "100%" }}
              />
            </div>
          </Menu.Item>
          <Menu.Item>
            <Checkbox
              checked={
                options.filter((it: OptionInterface) => it.selected !== true)
                  .length === 0
              }
              label="All Branches"
              onChange={() => toggleOption("all")}
            />
          </Menu.Item>
          {employers
            .filter((employer) =>
              employer.company_name
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
            )
            .map((employer) => (
              <Menu.Item key={employer.id}>
                <Checkbox
                  checked={
                    options.filter(
                      (it: OptionInterface) => it.value === employer.id
                    )[0]?.selected
                  }
                  label={employer.company_name}
                  onChange={() => toggleOption(employer.id)}
                />
              </Menu.Item>
            ))}
          {/* Disable if all are not selected */}
          <Button
            fullWidth
            onClick={applyFilter}
            disabled={
              options.filter((it: OptionInterface) => it.selected).length === 0
            }
          >
            Apply Filters
          </Button>
        </Menu>
      </div>
    </div>
  );
};

export default EmployerReportFilter;
